import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import type React from 'react'
import { api, downloadBlob } from '../api/client'
import { attributeCell, columnLabel } from '../attributes'
import type { CISource, ObservedAttribute, Taxonomy } from '../api/types'
import {
  Card,
  EmptyState,
  ErrorBanner,
  etichettaCicloVita,
  etichettaRuolo,
  LIFECYCLE_LABEL,
  LifecycleBadge,
  ROLE_LABEL,
  RoleBadge,
  Spinner,
  StatusBadge,
  timeAgo,
} from '../components/common'
import { Donut } from '../components/charts'
import { useAsync } from '../hooks/useAsync'
import { usePermessiModuli } from '../auth/permessi'
import { useAuth } from '../auth/AuthContext'
import { useEagle } from '../hooks/useEagle'
import { DoppioniCi } from '../components/DoppioniCi'
import { FinestraParametri, applicaInLotti, testoEsito } from '../components/EagleProfili'
import { Scorrevole } from '../components/Scorrevole'
import { CellaTag, MenuTagFiltro, TagChip, TagFiltrati, useCatalogoTag } from '../components/Tag'
import { localeCorrente, t } from '../i18n/lingua'
import { CRITICALITY_LABEL } from './Services'

/** Quanto ci si può fidare della classe attribuita a un CI. */
const CONFIDENCE_HINT: Record<string, () => string> = {
  high: () => t('Tipo dichiarato dalla sorgente o riconoscimento inequivocabile'),
  medium: () => t('Dedotto dalla descrizione di sistema o dai bit di sysServices'),
  low: () => t('Nessuna regola ha riconosciuto questo CI'),
}

/**
 * Attributi che l'elenco mostra già in una colonna propria.
 *
 * Vanno esclusi da quelle dinamiche della classe, o compaiono due volte: è
 * successo con `vendor`, affiancato a «Produttore» con lo stesso valore.
 */
const FIXED_COLUMNS = new Set(['platform', 'provider', 'mgmt_ip', 'vendor'])

/**
 * Le colonne che si possono mostrare o nascondere dal menu «Colonne». Il nome
 * resta sempre (è il link alla scheda). Le «extra» sono attributi comuni che non
 * hanno una colonna di base: spente finché qualcuno non le chiede. La scelta
 * resta nel browser di chi guarda.
 */
const COLONNE_BASE: Array<{ id: string; label: () => string }> = [
  { id: 'classe', label: () => t('Classe') },
  { id: 'tag', label: () => t('Tag') },
  { id: 'attributi', label: () => t('Attributi della classe') },
  { id: 'vendor', label: () => t('Produttore') },
  { id: 'origine', label: () => t('Origine') },
  { id: 'chiave', label: () => t('Chiave naturale') },
  { id: 'indirizzo', label: () => t('Indirizzo') },
  { id: 'visto', label: () => t('Visto') },
]
const COLONNE_EXTRA: Array<{ id: string; label: () => string }> = [
  { id: 'model', label: () => t('Modello') },
  { id: 'serial', label: () => t('Serial Number') },
  { id: 'os', label: () => t('Sistema operativo') },
  { id: 'os_version', label: () => t('Versione del sistema') },
  { id: 'software_rev', label: () => t('Versione software') },
  { id: 'dns_name', label: () => t('Nome DNS') },
  { id: 'sys_location', label: () => t('Ubicazione') },
  { id: 'criticita', label: () => t('Criticità ereditata') },
  // Where the CI is plugged in, from the connected_to relations (MAC table,
  // LLDP): not an attribute, so no sort and no column filter.
  { id: 'switch', label: () => t('Switch') },
  { id: 'porta', label: () => t('Porta dello switch') },
]
/** The extra columns that are not attributes of the CI. */
const NON_ATTRIBUTI = new Set(['criticita', 'switch', 'porta'])
const CHIAVE_COLONNE = 'censo.cis.colonne'
type SceltaColonne = { nascoste: string[]; extra: string[] }

function leggiColonne(): SceltaColonne {
  try {
    const v = JSON.parse(localStorage.getItem(CHIAVE_COLONNE) ?? 'null')
    if (v && Array.isArray(v.nascoste) && Array.isArray(v.extra)) return v
  } catch {
    // memoria del browser assente o illeggibile: si parte dalle predefinite
  }
  return { nascoste: [], extra: [] }
}

/** Il menu «Colonne»: le colonne di base da mostrare e le extra da aggiungere. */
function MenuColonne({ scelta, onCambia }: { scelta: SceltaColonne; onCambia: (s: SceltaColonne) => void }) {
  const [aperto, setAperto] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!aperto) return
    const fuori = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setAperto(false)
    }
    document.addEventListener('mousedown', fuori)
    return () => document.removeEventListener('mousedown', fuori)
  }, [aperto])
  const alternaBase = (id: string) =>
    onCambia({ ...scelta, nascoste: scelta.nascoste.includes(id) ? scelta.nascoste.filter((x) => x !== id) : [...scelta.nascoste, id] })
  const alternaExtra = (id: string) =>
    onCambia({ ...scelta, extra: scelta.extra.includes(id) ? scelta.extra.filter((x) => x !== id) : [...scelta.extra, id] })
  return (
    <div className="colonne-menu" ref={box}>
      <button type="button" className="secondary" onClick={() => setAperto((v) => !v)} aria-expanded={aperto}>
        {t('Colonne')}
      </button>
      {aperto && (
        <div className="colonne-menu__pannello">
          <strong>{t('Colonne mostrate')}</strong>
          {COLONNE_BASE.map((c) => (
            <label key={c.id}>
              <input type="checkbox" checked={!scelta.nascoste.includes(c.id)} onChange={() => alternaBase(c.id)} />
              {c.label()}
            </label>
          ))}
          <strong style={{ marginTop: 6 }}>{t('Colonne in più')}</strong>
          {COLONNE_EXTRA.map((c) => (
            <label key={c.id}>
              <input type="checkbox" checked={scelta.extra.includes(c.id)} onChange={() => alternaExtra(c.id)} />
              {c.label()}
            </label>
          ))}
          <button type="button" className="link" style={{ alignSelf: 'flex-start', marginTop: 4 }}
                  onClick={() => onCambia({ nascoste: [], extra: [] })}>
            {t('Ripristina le colonne predefinite')}
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * Il filtro per colonna con condizioni, lato server.
 *
 * A differenza della tabella riusabile — che filtra le righe già in memoria — la
 * lista dei CI è paginata dal server: filtrare solo le righe visibili mostrerebbe
 * un sottoinsieme spacciato per il risultato. Perciò le condizioni viaggiano fino
 * all'API (parametro `filters`), che le applica su TUTTE le pagine e restituisce
 * il conteggio esatto. Gli operatori sono gli stessi della tabella riusabile.
 */
type Op = 'contains' | 'not_contains' | 'equals' | 'starts' | 'gt' | 'gte' | 'lt' | 'lte' | 'ne'
type ColFiltro = { op: Op; value: string }

const OPS_TESTO: { key: Op; label: () => string }[] = [
  { key: 'contains', label: () => t('Contiene') },
  { key: 'not_contains', label: () => t('Non contiene') },
  { key: 'equals', label: () => t('È uguale a') },
  { key: 'starts', label: () => t('Inizia con') },
]
const OPS_DATA: { key: Op; label: () => string }[] = [
  { key: 'gt', label: () => t('Dopo il') },
  { key: 'lt', label: () => t('Prima del') },
]

/** Le condizioni non vuote come JSON per l'API, o stringa vuota se nessuna. */
function serializzaFiltri(colFiltri: Record<string, ColFiltro>): string {
  const condizioni = Object.entries(colFiltri)
    .filter(([, f]) => f.value.trim() !== '')
    .map(([col, f]) => ({ col, op: f.op, value: f.value.trim() }))
  return condizioni.length ? JSON.stringify(condizioni) : ''
}

/** Rilegge le condizioni dall'URL, tollerante a un parametro malformato. */
function leggiFiltri(raw: string | null): Record<string, ColFiltro> {
  if (!raw) return {}
  try {
    const arr = JSON.parse(raw)
    if (!Array.isArray(arr)) return {}
    const fuori: Record<string, ColFiltro> = {}
    for (const c of arr) {
      if (c && typeof c.col === 'string') fuori[c.col] = { op: c.op, value: String(c.value ?? '') }
    }
    return fuori
  } catch {
    return {}
  }
}

/** Una cella di filtro sotto l'intestazione: operatore + valore, per una colonna. */
function CellaFiltro({
  tipo,
  filtro,
  onChange,
}: {
  tipo: 'text' | 'date'
  filtro: ColFiltro | undefined
  onChange: (f: ColFiltro) => void
}) {
  const ops = tipo === 'date' ? OPS_DATA : OPS_TESTO
  const op = filtro?.op ?? ops[0].key
  const value = filtro?.value ?? ''
  return (
    <th className="colfiltro">
      <div className="colfiltro__cella">
        <select value={op} onChange={(e) => onChange({ op: e.target.value as Op, value })}>
          {ops.map((o) => (
            <option key={o.key} value={o.key}>
              {o.label()}
            </option>
          ))}
        </select>
        <input
          type={tipo === 'date' ? 'date' : 'search'}
          value={value}
          placeholder={tipo === 'date' ? '' : t('filtra…')}
          onChange={(e) => onChange({ op, value: e.target.value })}
        />
      </div>
    </th>
  )
}

/** Etichetta leggibile di un'integrazione, con ricaduta sulla chiave. */
function sourceLabel(known: CISource[] | null, key: string | null): string {
  if (!key) return '—'
  const label = known?.find((s) => s.source === key)?.label
  return label ? t(label) : key
}

/**
 * Il modulo del CI dichiarato: la macchina che nessuna scansione raggiunge.
 *
 * Le chiavi naturali (seriale, indirizzo, nome host) non sono cosmesi: sono
 * ciò che permetterà alla discovery, quando arriverà davvero, di RICONOSCERE
 * questo CI e arricchirlo invece di crearne un doppione. Un CI senza chiavi si
 * accetta — esiste, e va scritto — ma il modulo lo dice prima.
 */
function NuovoCi({
  taxonomy,
  onDone,
}: {
  taxonomy: Taxonomy | null | undefined
  onDone: () => void
}) {
  const navigate = useNavigate()
  const [nome, setNome] = useState('')
  const [classe, setClasse] = useState('server')
  const [seriale, setSeriale] = useState('')
  const [indirizzo, setIndirizzo] = useState('')
  const [hostname, setHostname] = useState('')
  const [mac, setMac] = useState('')
  const [subnet, setSubnet] = useState('')
  const [note, setNote] = useState('')
  const [esitoSubnet, setEsitoSubnet] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const senzaChiavi =
    !seriale.trim() && !indirizzo.trim() && !hostname.trim() && !mac.trim()

  async function dichiaraIp() {
    setBusy(true)
    setError(null)
    setEsitoSubnet(null)
    try {
      const esito = await api.declarePublicIps(
        subnet.split(/[\s,;]+/).filter(Boolean),
        nome.trim() || undefined,
        note.trim() || undefined,
      )
      setEsitoSubnet(
        t('{creati} indirizzi creati, {esistenti} già presenti.', {
          creati: esito.creati,
          esistenti: esito.esistenti,
        }),
      )
      setSubnet('')
      navigate('/cis?class=public_ip')
    } catch (err) {
      setError(err)
      setBusy(false)
      return
    }
    setBusy(false)
  }

  async function crea() {
    setBusy(true)
    setError(null)
    try {
      const ci = await api.createCi({
        name: nome,
        ci_class: classe,
        serial: seriale.trim() || undefined,
        mgmt_ip: indirizzo.trim() || undefined,
        hostname: hostname.trim() || undefined,
        mac: mac.trim() || undefined,
      })
      onDone()
      navigate(`/cis/${ci.id}`)
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }

  return (
    <Card title={t('Nuovo CI')}>
      <ErrorBanner error={error} />
      <p className="text-muted" style={{ marginTop: 0, maxWidth: 720 }}>
        {t('Per la macchina che nessuna scansione raggiunge: la sede staccata, l’apparato ordinato e non ancora in rete. Le chiavi qui sotto permettono alla discovery, quando arriverà, di riconoscerlo invece di duplicarlo.')}
      </p>

      <div className="grid grid--2" style={{ maxWidth: 720 }}>
        <div className="field">
          <label htmlFor="nuovo-classe">{t('Classe')}</label>
          <select id="nuovo-classe" value={classe} onChange={(e) => setClasse(e.target.value)}>
            {(taxonomy?.categories ?? []).map((cat) => (
              <optgroup key={cat.key} label={t(cat.label)}>
                {cat.classes.map((c) => (
                  <option key={c.key} value={c.key}>
                    {t(c.label)}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="nuovo-nome">{t('Nome')}</label>
          <input
            id="nuovo-nome"
            value={nome}
            placeholder={classe === 'public_ip' ? t('es. Uffici Milano') : t('es. srv-sede-nord')}
            onChange={(e) => setNome(e.target.value)}
          />
        </div>
      </div>

      {classe !== 'public_ip' && (
        <>
          <p className="form__sezione" style={{ maxWidth: 720 }}>{t('Chiavi per il riconoscimento')}</p>
          <div className="grid grid--2" style={{ maxWidth: 720 }}>
            <div className="field">
              <label htmlFor="nuovo-seriale">{t('Numero di serie')}</label>
              <input
                id="nuovo-seriale"
                value={seriale}
                onChange={(e) => setSeriale(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="nuovo-mac">{t('Indirizzo MAC')}</label>
              {/* Su stampanti e access point è sull'etichetta: spesso è
                  l'unica chiave che si ha in mano. Qualunque grafia va bene. */}
              <input
                id="nuovo-mac"
                value={mac}
                placeholder="aa:bb:cc:00:11:22"
                onChange={(e) => setMac(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="nuovo-ip">{t('Indirizzo di gestione')}</label>
              <input
                id="nuovo-ip"
                value={indirizzo}
                placeholder="10.0.10.12"
                onChange={(e) => setIndirizzo(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="nuovo-host">{t('Nome host')}</label>
              <input
                id="nuovo-host"
                value={hostname}
                placeholder={t('srv-nord-01')}
                onChange={(e) => setHostname(e.target.value)}
              />
            </div>
          </div>
        </>
      )}

      {classe === 'public_ip' && (
        <>
          <p className="form__sezione" style={{ maxWidth: 720 }}>{t('Indirizzi da dichiarare')}</p>
          <div style={{ maxWidth: 720 }}>
            {/* Un indirizzo pubblico non è un apparato: niente seriale o MAC.
                Si ragiona a subnet — un CI solo con la tabella dentro — e
                l'indirizzo singolo resta possibile. */}
            <div className="field">
              <label htmlFor="nuovo-subnet">{t('Indirizzi o subnet (CIDR), separati da virgola')}</label>
              <input
                id="nuovo-subnet"
                value={subnet}
                placeholder="185.60.216.0/28, 203.0.113.7"
                onChange={(e) => setSubnet(e.target.value)}
              />
              <p className="field__hint">
                {t('Una subnet diventa UN CI, con la tabella dei suoi indirizzi sulla scheda (fino a una /24): la verifica di reputazione la riempie indirizzo per indirizzo. Un indirizzo scritto da solo diventa un CI suo.')}
              </p>
            </div>
            <div className="field">
              <label htmlFor="nuovo-note">{t('Note')}</label>
              <input
                id="nuovo-note"
                value={note}
                placeholder={t('a cosa serve, chi la gestisce…')}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          </div>
        </>
      )}
      {senzaChiavi && classe !== 'public_ip' && (
        <p className="field__hint">
          {t('Senza seriale, indirizzo o nome host il CI si crea lo stesso, ma la discovery non avrà modo di riconoscerlo: se la macchina verrà scandita, diventerà un doppione.')}
        </p>
      )}
      {esitoSubnet && <p className="field__hint">{esitoSubnet}</p>}
      <div className="rowactions" style={{ textAlign: 'left', marginTop: 12 }}>
        {classe === 'public_ip' ? (
          <>
          <button onClick={dichiaraIp} disabled={busy || !subnet.trim()}>
            {busy ? t('Creazione…') : t('Dichiara')}
          </button>
          {/* Il contrario di «dichiaro una subnet» è «la tolgo», in un colpo:
              toglie indirizzi e subnet dichiarati che ricadono nelle voci.
              Gli indirizzi agganciati a un host restano. */}
          <button
            className="danger"
            disabled={busy || !subnet.trim()}
            onClick={async () => {
              setBusy(true)
              setError(null)
              try {
                const esito = await api.cleanupPublicIps(
                  subnet.split(/[\s,;]+/).filter(Boolean),
                )
                setEsitoSubnet(t('{tolti} voci tolte dalla CMDB.', { tolti: esito.tolti }))
                setSubnet('')
              } catch (err) {
                setError(err)
              } finally {
                setBusy(false)
              }
            }}
          >
            {t('Togli dalla CMDB')}
          </button>
          </>
        ) : (
        <button onClick={crea} disabled={busy || !nome.trim()}>
          {busy ? t('Creazione…') : t('Crea')}
        </button>
        )}
        <button className="secondary" onClick={onDone} disabled={busy}>
          {t('Annulla')}
        </button>
      </div>
    </Card>
  )
}


/** Colori dei grafici: una rotazione stabile, così la stessa fetta ha sempre la
 *  stessa tinta fra un caricamento e l'altro. */
const COLORI_CRUSCOTTO = [
  '#3b82f6', '#22c55e', '#f59e0b', '#a855f7', '#ef4444',
  '#14b8a6', '#eab308', '#ec4899', '#64748b', '#06b6d4',
]

/**
 * Il cruscotto in cima alla lista CI: sei pannelli che rispondono, in un colpo
 * d'occhio, alle domande con cui si apre l'inventario — quanti sono, come si
 * distribuiscono, cosa non torna. I pannelli numerici **filtrano la lista** al
 * clic (o portano alla pagina giusta): un pannello che non porta da nessuna
 * parte è una decorazione.
 */
function CruscottoCI({
  taxonomy,
  setParams,
  navigate,
  onNonVisti,
}: {
  taxonomy: Taxonomy | null
  setParams: (init: Record<string, string>) => void
  navigate: (to: string) => void
  /** Filters the list on «last seen more than 7 days ago». */
  onNonVisti: () => void
}) {
  const overview = useAsync(() => api.ciOverview(), [])
  const o = overview.data
  if (!o) return null

  // Il nome leggibile di una categoria, dal catalogo già in pagina; se manca
  // (una categoria tolta dal catalogo ma ancora su dei CI) si mostra la chiave.
  const etichettaCat: Record<string, string> = {}
  for (const c of taxonomy?.categories ?? []) etichettaCat[c.key] = t(c.label)

  const fetteCategoria = o.per_categoria.slice(0, 8).map((c, i) => ({
    key: c.key,
    label: etichettaCat[c.key] || c.key,
    value: c.count,
    color: COLORI_CRUSCOTTO[i % COLORI_CRUSCOTTO.length],
  }))
  const fetteOrigine = o.per_origine.slice(0, 8).map((c, i) => ({
    key: c.source,
    label: c.source,
    value: c.count,
    color: COLORI_CRUSCOTTO[i % COLORI_CRUSCOTTO.length],
  }))

  const severita = o.reperti.per_severita
  const reperti_riassunto = ['critical', 'high', 'medium', 'low']
    .filter((s) => severita[s])
    .map((s) => `${severita[s]} ${s}`)
    .join(' · ')

  return (
    <div className="widgets" style={{ marginBottom: 20 }}>
      <button className="stat" onClick={() => setParams({})} title={t('Mostra tutti')}>
        <div className="stat__value">{o.totale.toLocaleString(localeCorrente())}</div>
        <div className="stat__label">{t('CI totali')}</div>
        <div className="stat__hint">
          {o.nuovi_7g > 0
            ? t('+{n} negli ultimi 7 giorni', { n: o.nuovi_7g })
            : t('nessun nuovo negli ultimi 7 giorni')}
        </div>
      </button>

      <button
        className={`stat${o.non_classificati > 0 ? ' stat--warn' : ''}`}
        onClick={() => setParams({ class: 'unknown' })}
        title={t('Filtra i non classificati')}
      >
        <div className="stat__value">{o.non_classificati.toLocaleString(localeCorrente())}</div>
        <div className="stat__label">{t('Non classificati')}</div>
        <div className="stat__hint">{t('un connettore produce qualcosa di nuovo')}</div>
      </button>

      <button
        className={`stat${o.reperti.ci > 0 ? ' stat--error' : ''}`}
        onClick={() => navigate('/security')}
        title={t('Vai alla sicurezza')}
      >
        <div className="stat__value">{o.reperti.ci.toLocaleString(localeCorrente())}</div>
        <div className="stat__label">{t('CI con rischi aperti')}</div>
        <div className="stat__hint">{reperti_riassunto || t('nessun rischio aperto')}</div>
      </button>

      <button
        className={`stat${o.non_visti_7g > 0 ? ' stat--warn' : ''}`}
        onClick={onNonVisti}
        title={t('Filtra i CI non visti da 7 giorni')}
      >
        <div className="stat__value">{o.non_visti_7g.toLocaleString(localeCorrente())}</div>
        <div className="stat__label">{t('Non visti da 7 giorni')}</div>
        <div className="stat__hint">{t('candidati a una verifica: spenti, o non più raggiunti')}</div>
      </button>

      <Card title={t('Per categoria')}>
        {fetteCategoria.length ? (
          <Donut slices={fetteCategoria} centreLabel={String(o.totale)} />
        ) : (
          <p className="text-muted">{t('Nessun dato')}</p>
        )}
      </Card>

      <Card title={t('Come sono stati scoperti')}>
        {fetteOrigine.length ? (
          <Donut slices={fetteOrigine} />
        ) : (
          <p className="text-muted">{t('Nessun dato')}</p>
        )}
      </Card>
    </div>
  )
}

export default function ConfigurationItems() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const category = params.get('category') ?? ''
  const ciClass = params.get('class') ?? ''
  // Il tipo dichiarato dalla sonda (es. `unmanaged_host`): ci si arriva dalla
  // dashboard («Host non gestiti»), e si toglie dal distintivo sopra l'elenco.
  const ciType = params.get('type') ?? ''
  const source = params.get('source') ?? ''
  const state = params.get('state') ?? ''
  const role = params.get('role') ?? ''
  // The cards' own filters: «not seen for 7 days» (CI list) and «CIs to
  // register» (Assets). In the URL so a card is a link to its list.
  const unseen = params.get('unseen') ?? ''
  const unregistered = params.get('unregistered') === '1'
  const criticita = params.get('criticality') ?? ''
  // More tags: all of them (default) or at least one (`?tag_mode=any`).
  const tagModo: 'all' | 'any' = params.get('tag_mode') === 'any' ? 'any' : 'all'
  const filtriCruscotto = {
    unseen_days: unseen || undefined,
    unregistered: unregistered ? 'true' : undefined,
    service_criticality: criticita || undefined,
    tag_mode: tagModo === 'any' ? 'any' : undefined,
  }
  const attribute = params.get('attribute') ?? ''
  const value = params.get('value') ?? ''
  const query = params.get('q') ?? ''
  const [search, setSearch] = useState(query)
  // Il filtro per colonna: bozza reattiva mentre si scrive, poi applicata con un
  // ritardo così non si parte una richiesta a ogni tasto. La bozza vive anche
  // nell'URL, per condividere una vista già filtrata.
  const [colFiltri, setColFiltri] = useState<Record<string, ColFiltro>>(() =>
    leggiFiltri(params.get('filters')),
  )
  const [mostraFiltriColonna, setMostraFiltriColonna] = useState(() => !!params.get('filters'))
  const [filtriApplicati, setFiltriApplicati] = useState(() => serializzaFiltri(leggiFiltri(params.get('filters'))))
  useEffect(() => {
    const bozza = serializzaFiltri(colFiltri)
    const id = setTimeout(() => setFiltriApplicati(bozza), 400)
    return () => clearTimeout(id)
  }, [colFiltri])
  // Quante righe per pagina, e da dove parte la pagina corrente. 25 di default:
  // apre in fretta e sta in una schermata; chi vuole di più lo alza. L'elenco
  // non caricava mai TUTTO (aveva un tetto), quindi oltre il tetto le righe
  // sparivano in silenzio: la paginazione rende esplicito «di quante ce ne
  // sono, queste», col totale del filtro accanto.
  const [perPage, setPerPage] = useState(25)
  const [offset, setOffset] = useState(0)
  // L'ordinamento vive nell'URL come i filtri: un elenco ordinato si passa
  // con un link. Vuoto = ultimo rilevamento, il più recente in cima.
  const sort = params.get('sort') ?? ''
  const dir: 'asc' | 'desc' = params.get('dir') === 'asc' ? 'asc' : 'desc'
  function ordina(col: string) {
    const nuovoDir = sort === col ? (dir === 'asc' ? 'desc' : 'asc') : 'asc'
    update({ sort: col, dir: nuovoDir })
    setOffset(0)
  }
  /** Un'intestazione ordinabile: clic per ordinare, di nuovo per invertire. */
  const Th = ({ col, children, className }: { col: string; children: React.ReactNode; className?: string }) => (
    <th
      className={`sortable${className ? ' ' + className : ''}`}
      onClick={() => ordina(col)}
      title={t('Ordina per questa colonna')}
      aria-sort={sort === col ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {children}
      <span className="sortable__mark">{sort === col ? (dir === 'asc' ? '▲' : '▼') : ''}</span>
    </th>
  )
  // I tag: il filtro (lato server, su tutte le pagine: «ambiente:produzione»,
  // più tag separati da virgola = tutti), la selezione per metterli in blocco.
  const permessiTag = usePermessiModuli()
  const vedeTag = permessiTag.vede('tags')
  const scriveTag = permessiTag.scrive('tags')
  // Le azioni in blocco sui CI (dismetti, classe, togli dalla CMDB) sono di chi
  // scrive il modulo CI; i tag in blocco di chi scrive i tag.
  const scriveCi = permessiTag.scrive('cis')
  const puoSelezionare = scriveCi || (vedeTag && scriveTag)
  // Il tag applicato vive nell'URL (`?tag=ambiente:produzione`): ci si arriva
  // dalla ricerca globale e dalla pagina Tag, e la vista si può passare. La
  // bozza scritta nel filtro di colonna lo raggiunge con un ritardo.
  const filtroTagApplicato = params.get('tag') ?? ''
  const [filtroTag, setFiltroTag] = useState(filtroTagApplicato)
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (filtroTag.trim() !== filtroTagApplicato) update({ tag: filtroTag.trim() })
    }, 400)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroTag])
  useEffect(() => { setFiltroTag(filtroTagApplicato) }, [filtroTagApplicato])
  const catalogoTag = useCatalogoTag()
  const eagle = useEagle()
  const [selezionati, setSelezionati] = useState<Set<string>>(new Set())
  const [sceltaColonne, setSceltaColonne] = useState<SceltaColonne>(leggiColonne)
  function cambiaColonne(nuova: SceltaColonne) {
    setSceltaColonne(nuova)
    try {
      localStorage.setItem(CHIAVE_COLONNE, JSON.stringify(nuova))
    } catch {
      // senza memoria del browser la scelta vale finché la pagina è aperta
    }
  }
  const mostra = (id: string) => !sceltaColonne.nascoste.includes(id)
  const extra = COLONNE_EXTRA.filter((c) => sceltaColonne.extra.includes(c.id))
  // L'elenco filtrato in Excel o PDF: gli stessi filtri e le stesse colonne
  // che si vedono. Un elenco che si può solo guardare non si porta in riunione.
  const [esportando, setEsportando] = useState<'xlsx' | 'pdf' | null>(null)
  const [erroreEsporta, setErroreEsporta] = useState<unknown>(null)
  async function esporta(fmt: 'xlsx' | 'pdf') {
    setEsportando(fmt)
    setErroreEsporta(null)
    try {
      // The natural key is very often the serial number: with the «Serial
      // Number» column in the export it is the same value twice.
      const conSeriale = extra.some((c) => c.id === 'serial')
      const colonne = [
        ...COLONNE_BASE.filter((c) => c.id !== 'attributi' && mostra(c.id) && (c.id !== 'tag' || vedeTag)
          && !(c.id === 'chiave' && conSeriale)).map((c) => c.id),
        ...extra.map((c) => c.id),
      ]
      const blob = await downloadBlob(api.cisExportPath({
        category: category || undefined, ci_class: ciClass || undefined, ci_type: ciType || undefined,
        source: source || undefined, state: state || undefined, role: role || undefined,
        attribute: attribute || undefined, value: value || undefined, q: query || undefined,
        filters: filtriApplicati || undefined, tag: filtroTagApplicato || undefined,
        ...filtriCruscotto,
        sort: sort || undefined, dir,
      }, fmt, colonne))
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `censo-ci.${fmt}`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      setErroreEsporta(e)
    } finally {
      setEsportando(null)
    }
  }
  // Cambiare un filtro riparte da pagina 1: restare all'offset di prima potrebbe
  // cadere oltre la fine del nuovo risultato e mostrare una pagina vuota di un
  // insieme che invece ha righe.
  useEffect(() => {
    setOffset(0)
    setSelezionati(new Set())
  }, [category, ciClass, ciType, source, state, role, attribute, value, query, perPage, filtriApplicati, filtroTagApplicato, tagModo, unseen, unregistered, criticita])
  // Le condizioni applicate risalgono nell'URL (rimpiazzo, non nuova pagina di
  // cronologia), così un link porta con sé la vista filtrata senza intasare il
  // pulsante «indietro».
  useEffect(() => {
    const attuale = params.get('filters') ?? ''
    if (attuale === filtriApplicati) return
    const next = new URLSearchParams(params)
    if (filtriApplicati) next.set('filters', filtriApplicati)
    else next.delete('filters')
    setParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtriApplicati])
  // La riclassificazione: ricalcola la classe dei CI **già in CMDB** con le
  // regole attuali. Serve dopo un aggiornamento delle regole — altrimenti una
  // correzione vale solo per ciò che viene scansionato dopo, e gli apparati già
  // raccolti restano «da verificare» finché qualcuno non li ripassa.
  const [reclassifying, setReclassifying] = useState(false)
  const [doppioni, setDoppioni] = useState(false)
  const { principal } = useAuth()
  const isAdmin =
    principal?.roles.includes('tenant_admin') || principal?.roles.includes('provider_admin')
  const [reclassified, setReclassified] = useState<{
    reclassified: number
    unchanged: number
    enriched: number
  } | null>(null)
  const [reclassifyError, setReclassifyError] = useState<Error | null>(null)

  const reclassify = async () => {
    setReclassifying(true)
    setReclassifyError(null)
    try {
      setReclassified(await api.reclassify())
      cis.reload()
      taxonomy.reload()
    } catch (err) {
      setReclassifyError(err instanceof Error ? err : new Error(String(err)))
    } finally {
      setReclassifying(false)
    }
  }

  const taxonomy = useAsync(() => api.taxonomy(), [])
  const [creando, setCreando] = useState(false)
  const sources = useAsync(() => api.ciSources(), [])
  // Lo schema si chiede solo con una classe scelta: è lì che i filtri dinamici
  // hanno senso, perché gli attributi di uno switch e di un bucket non si
  // somigliano.
  const schema = useAsync(() => (ciClass ? api.observedSchema(ciClass) : Promise.resolve([])), [ciClass])
  const cis = useAsync(
    () =>
      api.cis({
        category: category || undefined,
        ci_class: ciClass || undefined,
        ci_type: ciType || undefined,
        source: source || undefined,
        state: state || undefined,
        role: role || undefined,
        attribute: attribute || undefined,
        value: value || undefined,
        q: query || undefined,
        filters: filtriApplicati || undefined,
        tag: filtroTagApplicato || undefined,
        ...filtriCruscotto,
        sort: sort || undefined,
        dir,
        limit: perPage,
        offset,
      }),
    [category, ciClass, ciType, source, state, role, attribute, value, query, filtriApplicati, filtroTagApplicato, tagModo, unseen, unregistered, criticita, sort, dir, perPage, offset],
  )
  // Il **totale** dei CI che rispettano i filtri in vigore. Separato dall'elenco
  // perché l'elenco è limitato (non si caricano pagine enormi) e la sua lunghezza
  // sarebbe il conteggio della pagina, non del filtro. Questo segue TUTTI i
  // filtri — non la sola categoria del menu a tendina.
  const conteggio = useAsync(
    () =>
      api.cisCount({
        category: category || undefined,
        ci_class: ciClass || undefined,
        ci_type: ciType || undefined,
        source: source || undefined,
        state: state || undefined,
        role: role || undefined,
        attribute: attribute || undefined,
        value: value || undefined,
        q: query || undefined,
        filters: filtriApplicati || undefined,
        tag: filtroTagApplicato || undefined,
        ...filtriCruscotto,
      }),
    [category, ciClass, ciType, source, state, role, attribute, value, query, filtriApplicati, filtroTagApplicato, tagModo, unseen, unregistered, criticita],
  )

  const setFiltroColonna = (col: string, f: ColFiltro) =>
    setColFiltri((prev) => ({ ...prev, [col]: f }))
  const azzeraFiltriColonna = () => setColFiltri({})
  const nFiltriColonna = Object.values(colFiltri).filter((f) => f.value.trim()).length

  function update(changes: Record<string, string>) {
    const next = new URLSearchParams(params)
    for (const [key, val] of Object.entries(changes)) {
      if (val) next.set(key, val)
      else next.delete(key)
    }
    setParams(next)
  }

  const categories = (taxonomy.data?.categories ?? []).filter((c) => c.count > 0)
  const classes = categories.flatMap((c) => c.classes).filter((k) => k.count > 0)

  // «Seleziona tutti quelli del filtro»: gli id li dà il server, non la
  // pagina — su un tag con trecento CI la selezione della pagina sarebbe
  // venticinque alla volta. Il tetto è quello delle azioni in blocco.
  const [selezionoTutti, setSelezionoTutti] = useState(false)
  async function selezionaTutti() {
    setSelezionoTutti(true)
    try {
      const r = await api.cisIds({
        category: category || undefined, ci_class: ciClass || undefined, ci_type: ciType || undefined,
        source: source || undefined, state: state || undefined, role: role || undefined,
        attribute: attribute || undefined, value: value || undefined, q: query || undefined,
        filters: filtriApplicati || undefined, tag: filtroTagApplicato || undefined,
        ...filtriCruscotto,
      })
      setSelezionati(new Set(r.ids))
    } catch {
      // l'errore lo mostra il prossimo caricamento; la selezione resta com'era
    } finally {
      setSelezionoTutti(false)
    }
  }

  // Le colonne che contano **dipendono dalla classe**: di un datastore si guarda
  // lo spazio, di un port group la VLAN, di una VM il sistema operativo. Con un
  // elenco misto non c'è una risposta giusta, quindi le colonne specifiche
  // compaiono solo quando si è scelta una classe — ed è la tassonomia a dire
  // quali sono, non un elenco scritto qui: un elenco nella console divergerebbe
  // da ciò che i connettori raccolgono.
  const columns: string[] = ciClass
    ? (classes.find((k) => k.key === ciClass)?.key_attributes ?? []).filter(
        // Gli attributi che hanno **già** una colonna fissa non si ripetono:
        // `platform` e `provider` stanno in Origine, `mgmt_ip` in Indirizzo,
        // `vendor` in Produttore. Su un firewall la tabella mostrava «Vendor» e
        // «Produttore» affiancate con lo stesso identico valore — due colonne
        // per un dato, e una in meno per ciò che non si vede altrove.
        (a) => !FIXED_COLUMNS.has(a),
      )
    : []
  const visibleClasses = category
    ? (categories.find((c) => c.key === category)?.classes ?? []).filter((k) => k.count > 0)
    : classes
  // Solo gli attributi con pochi valori distinti diventano un elenco a scelta:
  // su un campo come l'indirizzo IP non aiuterebbe.
  const filterable = (schema.data ?? []).filter(
    (a: ObservedAttribute) => !a.high_cardinality && a.sample_values.length > 1,
  )

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Configuration Item')}</h1>
          <p className="page__subtitle">{t('Mostra gli asset nuovi, non più rilevati, attivi e silenti.')}</p>
        </div>
        <div className="rowactions">
          <button onClick={() => setCreando((v) => !v)}>{t('Nuovo CI')}</button>
          <button className="secondary" disabled={reclassifying} onClick={reclassify}>
            {reclassifying ? t('Riclassifico…') : t('Riclassifica')}
          </button>
          {/* Unire doppioni cancella righe: solo per chi amministra il tenant. */}
          {isAdmin && (
            <button className="secondary" onClick={() => setDoppioni((v) => !v)}>
              {t('Trova doppioni')}
            </button>
          )}
        </div>
      </header>

      {doppioni && (
        <DoppioniCi
          onChiudi={() => setDoppioni(false)}
          onUniti={() => {
            void cis.reload()
            void taxonomy.reload()
          }}
        />
      )}

      <CruscottoCI taxonomy={taxonomy.data} setParams={setParams} navigate={navigate}
                   onNonVisti={() => setParams({ unseen: '7' })} />
      {(unseen || unregistered) && (
        <div className="banner banner--info" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span>
            {unseen && t('Solo i CI non visti da {n} giorni, esclusi i dismessi.', { n: unseen })}
            {unregistered && t('Solo i CI non ancora registrati come asset.')}
          </span>
          <button type="button" className="secondary" onClick={() => update({ unseen: '', unregistered: '' })}>
            {t('Togli il filtro')}
          </button>
        </div>
      )}

      {creando && (
        <NuovoCi
          taxonomy={taxonomy.data}
          onDone={() => {
            setCreando(false)
          }}
        />
      )}

      {/*
        La riclassificazione è ripetibile senza conseguenze — non tocca identità
        né chiavi naturali, cambia solo come un CI si legge — ma va comunque
        detto cosa ha fatto: «fatto» senza numeri lascia il dubbio che non abbia
        fatto niente, che è esattamente il caso in cui la si rilancia a vuoto.
      */}
      {reclassified && (
        <div className="banner banner--info">
          {t('Riclassificati {n} CI, {invariati} invariati.', {
            n: reclassified.reclassified,
            invariati: reclassified.unchanged,
          })}
          {/* Il secondo numero è un'operazione diversa dalla prima, e va detta:
              modello e versione ricavati dalla descrizione di sistema per gli
              apparati che non li espongono in una MIB. Tacerla farebbe sembrare
              che quei campi siano comparsi da soli. */}
          {reclassified.enriched > 0 &&
            ' ' +
              t('Su {n} CI sono stati ricavati modello o versione dalla descrizione di sistema.', {
                n: reclassified.enriched,
              })}
          {reclassified.reclassified === 0 &&
            reclassified.enriched === 0 &&
            ' ' + t('Nessun cambiamento: le regole attuali danno lo stesso esito di prima.')}
        </div>
      )}

      <ErrorBanner error={cis.error ?? taxonomy.error ?? reclassifyError} />
      <ErrorBanner error={erroreEsporta} />

      {taxonomy.data && taxonomy.data.undeclared.length > 0 && (
        <div className="banner banner--info">
          {t('Alcuni CI hanno una classe non prevista dal catalogo: {classi}. È il segnale che un connettore produce qualcosa di nuovo.', {
            classi: taxonomy.data.undeclared.map((u) => `${u.key} (${u.count})`).join(', '),
          })}
        </div>
      )}

      <div className="toolbar">
        <div className="field" style={{ flex: '2 1 240px' }}>
          <label htmlFor="q">{t('Ricerca')}</label>
          <input
            id="q"
            type="text"
            value={search}
            placeholder={t('Nome o chiave naturale')}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && update({ q: search })}
          />
        </div>
        <div className="field" style={{ flex: '1 1 180px' }}>
          <label htmlFor="category">{t('Categoria')}</label>
          <select
            id="category"
            value={category}
            onChange={(e) => update({ category: e.target.value, class: '', attribute: '', value: '' })}
          >
            <option value="">{t('Tutte')}</option>
            {categories.map((c) => (
              <option key={c.key} value={c.key}>
                {t(c.label)} ({c.count})
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ flex: '1 1 180px' }}>
          <label htmlFor="class">{t('Classe')}</label>
          <select
            id="class"
            value={ciClass}
            onChange={(e) => update({ class: e.target.value, attribute: '', value: '' })}
          >
            <option value="">{t('Tutte')}</option>
            {visibleClasses.map((k) => (
              <option key={k.key} value={k.key}>
                {t(k.label)} ({k.count})
              </option>
            ))}
          </select>
        </div>
        {ciType && (
          <div className="field" style={{ flex: '1 1 180px' }}>
            <label>{t('Tipo')}</label>
            <span className="chip">
              {ciType === 'unmanaged_host' ? t('Host non gestiti') : ciType}{' '}
              <button type="button" className="link" aria-label={t('Togli il filtro')} onClick={() => update({ type: '' })}>×</button>
            </span>
          </div>
        )}
        {vedeTag && (
          <div className="field" style={{ flex: '1 1 180px' }}>
            <label>{t('Tag')}</label>
            <MenuTagFiltro valore={filtroTagApplicato} catalogo={catalogoTag.data ?? []} tipo="ci"
                           onCambia={(v) => { setFiltroTag(v); update({ tag: v }) }} />
          </div>
        )}
        <div className="field" style={{ flex: '1 1 180px' }}>
          <label htmlFor="source">{t('Integrazione')}</label>
          <select id="source" value={source} onChange={(e) => update({ source: e.target.value })}>
            <option value="">{t('Tutte')}</option>
            {(sources.data ?? []).map((s) => (
              <option key={s.source} value={s.source}>
                {t(s.label)} ({s.also_seen})
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ flex: '1 1 160px' }}>
          <label htmlFor="state">{t('Stato')}</label>
          {/* Nessun filtro predefinito: i CI dismessi compaiono con gli altri,
              con il loro contrassegno. Nasconderli d'ufficio farebbe sparire
              degli oggetti da un elenco senza dirlo, e chi non trova più una
              macchina non saprebbe se è stata dismessa, cancellata o mai
              censita — tre casi con tre risposte diverse. */}
          <select id="state" value={state} onChange={(e) => update({ state: e.target.value })}>
            <option value="">{t('Tutti')}</option>
            {Object.entries(LIFECYCLE_LABEL).map(([key, label]) => (
              <option key={key} value={key}>
                {etichettaCicloVita(key) ?? label}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ flex: '1 1 200px' }}>
          <label htmlFor="role">{t('Ruolo')}</label>
          {/* Il ruolo è una dimensione diversa dalla classe, non un suo
              sottoinsieme: «ospita macchine virtuali» attraversa i server, i
              nodi Proxmox e gli host ESXi, che sono classi diverse. Un filtro a
              parte è l'unico modo di porre la domanda «su cosa gira la
              virtualizzazione» senza sapere già la risposta. */}
          <select id="role" value={role} onChange={(e) => update({ role: e.target.value })}>
            <option value="">{t('Tutti')}</option>
            {Object.entries(ROLE_LABEL).map(([key, label]) => (
              <option key={key} value={key}>
                {etichettaRuolo(key) ?? label}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ flex: '1 1 160px' }}>
          <label htmlFor="criticality">{t('Criticità')}</label>
          {/* Inherited from the business services the CI is part of. */}
          <select id="criticality" value={criticita} onChange={(e) => update({ criticality: e.target.value })}>
            <option value="">{t('Tutte')}</option>
            {Object.entries(CRITICALITY_LABEL).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
        <button onClick={() => update({ q: search })}>{t('Filtra')}</button>
        <button className="secondary" onClick={() => cis.reload()}>
          {t('Aggiorna')}
        </button>
      </div>

      {ciClass && filterable.length > 0 && (
        <div className="toolbar">
          <div className="field" style={{ flex: '1 1 200px' }}>
            <label htmlFor="attribute">{t('Attributo')}</label>
            <select
              id="attribute"
              value={attribute}
              onChange={(e) => update({ attribute: e.target.value, value: '' })}
            >
              <option value="">{t('Nessuno')}</option>
              {filterable.map((a) => (
                <option key={a.attribute} value={a.attribute}>
                  {a.attribute} ({a.occurrences})
                </option>
              ))}
            </select>
            <p className="field__hint">
              {t('Campi osservati per questa classe: nessuno li ha dichiarati in anticipo.')}
            </p>
          </div>
          {attribute && (
            <div className="field" style={{ flex: '1 1 200px' }}>
              <label htmlFor="value">{t('Valore')}</label>
              <select id="value" value={value} onChange={(e) => update({ value: e.target.value })}>
                <option value="">{t('Qualsiasi')}</option>
                {(filterable.find((a) => a.attribute === attribute)?.sample_values ?? []).map(
                  (v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ),
                )}
              </select>
            </div>
          )}
        </div>
      )}

      <Card
        title={
          conteggio.data
            ? t('Configuration Item ({n})', { n: conteggio.data.total })
            : t('Configuration Item')
        }
        accanto={vedeTag && (
          <TagFiltrati valore={filtroTagApplicato} modo={tagModo} catalogo={catalogoTag.data ?? []}
                       onCambia={(v, m) => { setFiltroTag(v); update({ tag: v, tag_mode: v && m === 'any' ? 'any' : '' }) }} />
        )}
        actions={
          <div className="rowactions">
            <button className="secondary" onClick={() => void esporta('xlsx')} disabled={esportando !== null}
                    title={t('Scarica l’elenco con i filtri in vigore in Excel')}>
              {esportando === 'xlsx' ? t('Scarico…') : t('Excel')}
            </button>
            <button className="secondary" onClick={() => void esporta('pdf')} disabled={esportando !== null}
                    title={t('Scarica l’elenco con i filtri in vigore in PDF')}>
              {esportando === 'pdf' ? t('Scarico…') : t('PDF')}
            </button>
            <MenuColonne scelta={sceltaColonne} onCambia={cambiaColonne} />
            {nFiltriColonna > 0 && (
              <button className="secondary" onClick={azzeraFiltriColonna}>
                {t('Azzera filtri')}
              </button>
            )}
            <button
              className={`secondary${mostraFiltriColonna ? ' is-active' : ''}`}
              onClick={() => setMostraFiltriColonna((v) => !v)}
            >
              {t('Filtri per colonna')}
              {nFiltriColonna > 0 ? ` (${nFiltriColonna})` : ''}
            </button>
          </div>
        }
      >
        {cis.loading && !cis.data ? (
          <Spinner />
        ) : !cis.data?.length ? (
          <EmptyState
            title={t('Nessun configuration item trovato')}
            hint={t('Avvia una discovery dalla sezione Discovery per popolare la CMDB.')}
          />
        ) : (
          <>
            {puoSelezionare && (conteggio.data?.total ?? 0) > selezionati.size && (
              <p className="muted" style={{ margin: '0 0 6px' }}>
                <button type="button" className="linklike" onClick={() => void selezionaTutti()} disabled={selezionoTutti}>
                  {selezionoTutti ? t('Seleziono…') : t('Seleziona tutti i {n} CI del filtro', { n: Math.min(conteggio.data?.total ?? 0, 1000) })}
                </button>
                {(conteggio.data?.total ?? 0) > 1000 && <span> · {t('Seleziona fino a 1.000 CI alla volta; per selezioni più mirate, restringi il filtro, ad esempio tramite tag.')}</span>}
              </p>
            )}
            {selezionati.size > 0 && (
              <div className="ci-blocco">
                <strong>{t('{n} CI selezionati', { n: selezionati.size })}</strong>
                {permessiTag.scrive('eagle') && (
                  <EagleInBlocco
                    ids={[...selezionati]}
                    attivo={eagle}
                    onFatto={() => {
                      setSelezionati(new Set())
                      void cis.reload()
                    }}
                  />
                )}
                {vedeTag && scriveTag && (
                  <TagInBlocco
                    ids={[...selezionati]}
                    onFatto={() => {
                      setSelezionati(new Set())
                      void cis.reload()
                    }}
                  />
                )}
                {scriveCi && (
                  <AzioniCiInBlocco
                    ids={[...selezionati]}
                    classi={(taxonomy.data?.categories ?? []).flatMap((c) => c.classes)}
                    onFatto={() => {
                      setSelezionati(new Set())
                      void cis.reload()
                      void taxonomy.reload()
                    }}
                  />
                )}
                <button type="button" className="link" onClick={() => setSelezionati(new Set())}>
                  {t('Annulla la selezione')}
                </button>
              </div>
            )}
            {/* La navigazione anche sopra la tabella: in fondo, sotto 25 righe o
                più, non la trovava nessuno. */}
            {conteggio.data && conteggio.data.total > perPage && (
              <div className="ci-pagine-su">
                <span className="text-muted">
                  {t('{da}–{a} di {total}', {
                    da: offset + 1,
                    a: offset + (cis.data?.length ?? 0),
                    total: conteggio.data.total,
                  })}
                </span>
                <button className="secondary" disabled={offset === 0}
                        onClick={() => setOffset(Math.max(0, offset - perPage))}>
                  {t('Precedenti')}
                </button>
                <button className="secondary" disabled={offset + perPage >= conteggio.data.total}
                        onClick={() => setOffset(offset + perPage)}>
                  {t('Successivi')}
                </button>
              </div>
            )}
            <Scorrevole>
            <table className="ci-tabella">
              <thead>
                <tr>
                  {puoSelezionare && (
                    <th className="ci-seleziona col-fissa">
                      <input
                        type="checkbox"
                        aria-label={t('Seleziona la pagina')}
                        checked={cis.data.length > 0 && cis.data.every((x) => selezionati.has(x.id))}
                        onChange={(e) =>
                          setSelezionati(e.target.checked ? new Set(cis.data!.map((x) => x.id)) : new Set())
                        }
                      />
                    </th>
                  )}
                  <Th col="name" className={`col-fissa${puoSelezionare ? ' col-fissa--dopo' : ''}`}>{t('Nome')}</Th>
                  {mostra('classe') && <Th col="ci_class">{t('Classe')}</Th>}
                  {vedeTag && mostra('tag') && <th>{t('Tag')}</th>}
                  {mostra('attributi') && columns.map((a) => (
                    <Th key={a} col={`attr:${a}`}>{columnLabel(a)}</Th>
                  ))}
                  {extra.map((c) => (NON_ATTRIBUTI.has(c.id)
                    // Inherited from business services, or the switch and
                    // port: not attributes, so no sort by attribute (the
                    // criticality filters with the «Criticità» filter above).
                    ? <th key={`x-${c.id}`}>{c.label()}</th>
                    : <Th key={`x-${c.id}`} col={`attr:${c.id}`}>{c.label()}</Th>
                  ))}
                  {mostra('vendor') && <Th col="vendor">{t('Produttore')}</Th>}
                  {mostra('origine') && <Th col="source">{t('Origine')}</Th>}
                  {mostra('chiave') && <Th col="primary_key">{t('Chiave naturale')}</Th>}
                  {mostra('indirizzo') && <Th col="mgmt_ip">{t('Indirizzo')}</Th>}
                  {mostra('visto') && <Th col="last_seen">{t('Visto')}</Th>}
                </tr>
                {mostraFiltriColonna && (
                  <tr className="colfiltro-row">
                    {puoSelezionare && <th className="col-fissa" />}
                    <CellaFiltro tipo="text" filtro={colFiltri['name']}
                                 onChange={(f) => setFiltroColonna('name', f)} />
                    {mostra('classe') && (
                      <CellaFiltro tipo="text" filtro={colFiltri['ci_class']}
                                   onChange={(f) => setFiltroColonna('ci_class', f)} />
                    )}
                    {vedeTag && mostra('tag') && (
                      <th>
                        <input
                          value={filtroTag}
                          onChange={(e) => setFiltroTag(e.target.value)}
                          placeholder={t('chiave:valore')}
                          aria-label={t('Filtra per tag (chiave:valore)')}
                          style={{ width: '100%', minWidth: 90, fontSize: 12, padding: '2px 4px' }}
                        />
                      </th>
                    )}
                    {mostra('attributi') && columns.map((a) => (
                      <CellaFiltro key={a} tipo="text" filtro={colFiltri[`attr:${a}`]}
                                   onChange={(f) => setFiltroColonna(`attr:${a}`, f)} />
                    ))}
                    {extra.map((c) => (NON_ATTRIBUTI.has(c.id)
                      ? <th key={`x-${c.id}`} />
                      : <CellaFiltro key={`x-${c.id}`} tipo="text" filtro={colFiltri[`attr:${c.id}`]}
                                     onChange={(f) => setFiltroColonna(`attr:${c.id}`, f)} />
                    ))}
                    {mostra('vendor') && (
                      <CellaFiltro tipo="text" filtro={colFiltri['vendor']}
                                   onChange={(f) => setFiltroColonna('vendor', f)} />
                    )}
                    {mostra('origine') && (
                      <CellaFiltro tipo="text" filtro={colFiltri['source']}
                                   onChange={(f) => setFiltroColonna('source', f)} />
                    )}
                    {mostra('chiave') && (
                      <CellaFiltro tipo="text" filtro={colFiltri['primary_key']}
                                   onChange={(f) => setFiltroColonna('primary_key', f)} />
                    )}
                    {mostra('indirizzo') && (
                      <CellaFiltro tipo="text" filtro={colFiltri['mgmt_ip']}
                                   onChange={(f) => setFiltroColonna('mgmt_ip', f)} />
                    )}
                    {mostra('visto') && (
                      <CellaFiltro tipo="date" filtro={colFiltri['last_seen']}
                                   onChange={(f) => setFiltroColonna('last_seen', f)} />
                    )}
                  </tr>
                )}
              </thead>
              <tbody>
                {cis.data.map((a) => {
                  const label =
                    (() => {
                      const etichetta = classes.find((k) => k.key === a.ci_class)?.label
                      return etichetta ? t(etichetta) : a.ci_class ?? a.ci_type
                    })()
                  return (
                    <tr key={a.id}>
                      {puoSelezionare && (
                        <td className="ci-seleziona col-fissa">
                          <input
                            type="checkbox"
                            aria-label={t('Seleziona {nome}', { nome: a.name ?? a.primary_key })}
                            checked={selezionati.has(a.id)}
                            onChange={(e) =>
                              setSelezionati((prima) => {
                                const dopo = new Set(prima)
                                if (e.target.checked) dopo.add(a.id)
                                else dopo.delete(a.id)
                                return dopo
                              })
                            }
                          />
                        </td>
                      )}
                      <td className={`col-fissa${puoSelezionare ? ' col-fissa--dopo' : ''}`}>
                        <Link to={`/cis/${a.id}`}>{a.name ?? t('(senza nome)')}</Link>{' '}
                        {/* Solo se non è attivo: un contrassegno «Attivo» su
                            ogni riga sarebbe rumore che nasconde le poche righe
                            che dicono qualcosa. */}
                        {a.lifecycle_state !== 'active' && (
                          <LifecycleBadge state={a.lifecycle_state} />
                        )}
                      </td>
                      {mostra('classe') && (
                      <td>
                        <StatusBadge status={label} />
                        {a.class_confidence === 'low' && (
                          <span className="chip" title={a.class_reason ?? CONFIDENCE_HINT.low()}>
                            {t('da verificare')}
                          </span>
                        )}
                        {/* I ruoli accanto alla classe e non in una colonna
                            propria: dicono della stessa cosa — che cos'è questo
                            oggetto — e una colonna vuota su nove righe su dieci
                            farebbe concludere che il dato non si raccoglie. */}
                        {Object.entries(a.roles ?? {}).map(([key, reason]) => (
                          <RoleBadge key={key} role={key} reason={reason} />
                        ))}
                      </td>
                      )}
                      {vedeTag && mostra('tag') && (
                        <td>
                          <CellaTag tipo="ci" id={a.id} tags={a.tags ?? []} scrive={scriveTag}
                                    onChanged={() => void cis.reload()} />
                        </td>
                      )}
                      {mostra('attributi') && columns.map((attr) => (
                        <td key={attr}>{attributeCell(a.attributes?.[attr], attr)}</td>
                      ))}
                      {extra.map((c) => (
                        <td key={`x-${c.id}`}>{c.id === 'criticita'
                          ? (a.service_criticality ? (CRITICALITY_LABEL[a.service_criticality] ?? a.service_criticality) : '—')
                          : c.id === 'switch'
                            ? (a.uplink ? <Link to={`/cis/${a.uplink.switch_id}`}>{a.uplink.switch_name}</Link> : '—')
                            : c.id === 'porta'
                              ? (a.uplink?.port ? <code>{a.uplink.port}</code> : '—')
                              : attributeCell(a.attributes?.[c.id], c.id)}</td>
                      ))}
                      {mostra('vendor') && <td>{a.vendor ?? '—'}</td>}
                      {mostra('origine') && (
                      <td>
                        {sourceLabel(sources.data, a.source)}
                        {Object.keys(a.sources ?? {}).length > 1 && (
                          <span
                            className="chip"
                            title={t('Visto anche da: {fonti}', {
                              fonti: Object.keys(a.sources)
                                .filter((k) => k !== a.source)
                                .map((k) => sourceLabel(sources.data, k))
                                .join(', '),
                            })}
                          >
                            +{Object.keys(a.sources).length - 1}
                          </span>
                        )}
                      </td>
                      )}
                      {mostra('chiave') && <td className="mono">{a.primary_key}</td>}
                      {mostra('indirizzo') && <td className="mono">{String(a.attributes.mgmt_ip ?? '—')}</td>}
                      {mostra('visto') && <td title={a.last_seen ?? ''}>{timeAgo(a.last_seen)}</td>}
                    </tr>
                  )
                })}
              </tbody>
            </table>
            </Scorrevole>
          </>
        )}

        {/* La barra della paginazione: quante righe per pagina, dove siamo nel
            totale del filtro, e avanti/indietro. Compare solo se c'è almeno una
            riga — su un elenco vuoto sarebbe rumore. */}
        {conteggio.data && conteggio.data.total > 0 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
              marginTop: 12,
            }}
          >
            <span className="text-muted">
              {t('{da}–{a} di {total}', {
                da: offset + 1,
                a: offset + (cis.data?.length ?? 0),
                total: conteggio.data.total,
              })}
            </span>
            <div className="rowactions" style={{ alignItems: 'center', gap: 8 }}>
              <label htmlFor="per-pagina" className="text-muted">
                {t('Per pagina')}
              </label>
              <select
                id="per-pagina"
                value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value))}
              >
                {[25, 50, 100, 200].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <button
                className="secondary"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - perPage))}
              >
                {t('Precedenti')}
              </button>
              <button
                className="secondary"
                disabled={offset + perPage >= conteggio.data.total}
                onClick={() => setOffset(offset + perPage)}
              >
                {t('Successivi')}
              </button>
            </div>
          </div>
        )}

        {(taxonomy.data?.low_confidence ?? 0) > 0 && (
          <p className="field__hint" style={{ marginTop: 12 }}>
            {t('{n} CI non sono stati riconosciuti da alcuna regola. Restano consultabili e classificati come «non classificati»: attribuirli a caso sarebbe peggio, perché l’errore si noterebbe solo a valle.', {
              n: taxonomy.data?.low_confidence,
            })}
          </p>
        )}
      </Card>
    </>
  )
}

/**
 * Eagle in blocco: mette in monitoraggio i CI selezionati col profilo della
 * loro classe (come «Attiva con il profilo» nella scheda), o lo toglie. Chi
 * non ha parametri di profilo si salta e si dice chi. Con Eagle non attivo il
 * pulsante c'è ma non si può premere: si vede cosa si avrebbe con l'add-on.
 */
function EagleInBlocco({ ids, attivo, onFatto }: { ids: string[]; attivo: boolean; onFatto: () => void }) {
  const [errore, setErrore] = useState<unknown>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const [lavoro, setLavoro] = useState(false)
  // The saved profiles, for «Applica profilo…»; the ad-hoc rules open in a window.
  const profili = useAsync(() => (attivo ? api.eagleProfili() : Promise.resolve(null)), [attivo])
  const [profilo, setProfilo] = useState('')
  const [editor, setEditor] = useState(false)

  async function fai(azione: 'enable' | 'disable') {
    setErrore(null)
    setEsito(null)
    setLavoro(true)
    try {
      const r = await api.bulkEagle(ids, azione)
      const saltati = r.skipped.length > 0
        ? ' ' + t('{m} saltati: {elenco}', { m: r.skipped.length, elenco: r.skipped.slice(0, 5).map((x) => `${x.name} (${x.reason})`).join(', ') + (r.skipped.length > 5 ? '…' : '') })
        : ''
      setEsito((azione === 'enable' ? t('{n} CI messi in monitoraggio.', { n: r.changed }) : t('{n} CI tolti dal monitoraggio.', { n: r.changed })) + saltati)
      onFatto()
    } catch (err) {
      setErrore(err)
    } finally {
      setLavoro(false)
    }
  }
  async function applicaProfilo() {
    if (!profilo) return
    setErrore(null)
    setEsito(null)
    setLavoro(true)
    try {
      const r = await applicaInLotti(ids, { profile_id: profilo })
      const nome = profili.data?.profiles.find((p) => p.id === profilo)?.name ?? ''
      setEsito(`${nome}: ${testoEsito(r)}`)
      onFatto()
    } catch (err) {
      setErrore(err)
    } finally {
      setLavoro(false)
    }
  }

  const motivo = attivo ? undefined : t('Eagle non è attivo per questo tenant')
  const elenco = profili.data?.licensed ? profili.data.profiles.filter((p) => p.enabled) : []
  return (
    <div className="tag-blocco">
      <span className="muted">{NOME_EAGLE}</span>
      <button type="button" disabled={lavoro || !attivo} title={motivo} onClick={() => void fai('enable')}>
        {t('Metti in monitoraggio')}
      </button>
      <button type="button" className="secondary" disabled={lavoro || !attivo} title={motivo} onClick={() => void fai('disable')}>
        {t('Togli dal monitoraggio')}
      </button>
      {attivo && (
        <>
          <select aria-label={t('Profilo di monitoraggio')} value={profilo} onChange={(e) => setProfilo(e.target.value)} disabled={lavoro}>
            <option value="">{elenco.length === 0 ? t('Nessun profilo (Eagle → Profili)') : t('Applica un profilo…')}</option>
            {elenco.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button type="button" className="secondary" disabled={lavoro || !profilo} onClick={() => void applicaProfilo()}>{t('Applica')}</button>
          <button type="button" className="secondary" disabled={lavoro} onClick={() => setEditor(true)} title={t('Regole al volo sui CI selezionati: accendi o spegni dei tipi di parametro, cambia soglie e frequenze.')}>
            {t('Parametri…')}
          </button>
        </>
      )}
      {!attivo && <span className="muted">{motivo}</span>}
      {esito && <span className="muted">{esito}</span>}
      <ErrorBanner error={errore} />
      {editor && <FinestraParametri ids={ids} onChiudi={() => setEditor(false)} onFatto={(testo) => { setEditor(false); setEsito(testo); onFatto() }} />}
    </div>
  )
}

const NOME_EAGLE = 'Eagle'

/**
 * Tag in blocco sui CI selezionati: metti un tag (nuovo o del catalogo) o
 * toglilo, su tutti insieme. La selezione è della pagina: per grandi insiemi
 * c'è la regola (pagina Tag), che vale anche per i CI che arriveranno.
 */
function TagInBlocco({ ids, onFatto }: { ids: string[]; onFatto: () => void }) {
  const catalogo = useCatalogoTag()
  const [testo, setTesto] = useState('')
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)

  async function fai(fare: () => Promise<unknown>) {
    setErrore(null)
    setLavoro(true)
    try {
      await fare()
      setTesto('')
      onFatto()
    } catch (err) {
      setErrore(err)
    } finally {
      setLavoro(false)
    }
  }

  const esistente = (catalogo.data ?? []).find((c) => c.label === testo.trim().toLowerCase() || c.label === testo.trim())
  return (
    <div className="tag-blocco">
      <span className="muted">{t('Tag')}</span>
      <input
        value={testo}
        onChange={(e) => setTesto(e.target.value)}
        placeholder={t('chiave:valore')}
        list="tag-blocco-catalogo"
        aria-label={t('Tag da mettere o togliere')}
      />
      <datalist id="tag-blocco-catalogo">
        {(catalogo.data ?? []).map((c) => (
          <option key={c.id} value={c.label} />
        ))}
      </datalist>
      {esistente && <TagChip tag={esistente} piccolo />}
      <button type="button" disabled={lavoro || !testo.trim()}
              onClick={() => void fai(() => api.assignTags('ci', ids, { tags: [testo.trim()] }))}>
        {t('Metti il tag')}
      </button>
      <button type="button" className="secondary" disabled={lavoro || !esistente}
              onClick={() => esistente && void fai(() => api.unassignTags('ci', ids, [esistente.id]))}>
        {t('Togli il tag')}
      </button>
      <ErrorBanner error={errore} />
    </div>
  )
}

/**
 * Le azioni sui CI selezionati, con le stesse regole della scheda del singolo
 * CI: «Dichiara dismesso» (con il motivo), «Annulla dismissione», la classe a
 * mano (o di nuovo automatica) e «Togli dalla CMDB» (per i record che non
 * dovevano esserci). Un'unica chiamata, `POST /cis/bulk`, tutto o niente.
 */
function AzioniCiInBlocco({
  ids,
  classi,
  onFatto,
}: {
  ids: string[]
  classi: { key: string; label: string }[]
  onFatto: () => void
}) {
  const [classe, setClasse] = useState('')
  const [errore, setErrore] = useState<unknown>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const [lavoro, setLavoro] = useState(false)

  async function fai(
    azione: 'retire' | 'unretire' | 'classify' | 'delete',
    extra: { note?: string; ci_class?: string | null } = {},
  ) {
    setErrore(null)
    setEsito(null)
    setLavoro(true)
    try {
      const r = await api.bulkCis({ ids, action: azione, ...extra })
      setEsito(t('{n} CI aggiornati.', { n: r.changed }))
      setClasse('')
      onFatto()
    } catch (err) {
      setErrore(err)
    } finally {
      setLavoro(false)
    }
  }

  function dismetti() {
    const nota = window.prompt(
      t('Dichiarare dismessi {n} CI? Lo stato resiste alle scansioni. Motivo (facoltativo):', { n: ids.length }),
      '',
    )
    if (nota === null) return
    void fai('retire', { note: nota })
  }

  function togli() {
    if (!window.confirm(t('Togliere {n} CI dalla CMDB?\n\nSe questi apparati esistono ancora in rete, la prossima scansione li riporta come oggetti nuovi, senza la loro storia: per macchine dismesse usa «Dichiara dismesso». Spariscono interfacce, software, relazioni e storico.', { n: ids.length }))) return
    void fai('delete')
  }

  return (
    <div className="tag-blocco">
      <span className="muted">{t('Azioni')}</span>
      <button type="button" className="secondary" disabled={lavoro} onClick={dismetti}>
        {t('Dichiara dismesso')}
      </button>
      <button type="button" className="secondary" disabled={lavoro} onClick={() => void fai('unretire')}>
        {t('Annulla dismissione')}
      </button>
      <select value={classe} onChange={(e) => setClasse(e.target.value)} aria-label={t('Classe da assegnare')}>
        <option value="">{t('Cambia classe…')}</option>
        <option value="__auto">{t('Automatica (dalle regole)')}</option>
        {classi.map((c) => (
          <option key={c.key} value={c.key}>{t(c.label)}</option>
        ))}
      </select>
      <button type="button" className="secondary" disabled={lavoro || !classe}
              onClick={() => void fai('classify', { ci_class: classe === '__auto' ? null : classe })}>
        {t('Applica classe')}
      </button>
      <button type="button" className="danger" disabled={lavoro} onClick={togli}>
        {t('Togli dalla CMDB')}
      </button>
      {esito && <span className="muted">{esito}</span>}
      <ErrorBanner error={errore} />
    </div>
  )
}

